import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * /the-method — THE CASH PAGE.
 * Showcases all 5 decks in ONE visual moment with the Full Method Bundle ($8.88)
 * as the hero CTA. Each deck has its own $2.22 Buy button too.
 *
 * Designed to be the link in every IG bio / story / email sig.
 */

const BRAND = {
  teal: "#068fa2",
  tealDeep: "#0a5b6a",
  tealInk: "#042f2e",
  cream: "#efeae7",
  gold: "#d4af6a",
};

// Live Gumroad URLs for all 5 decks + bundle.
const GUMROAD_URLS = {
  iGetTo: "https://templefranklin22.gumroad.com/l/wscmew",
  rewire: "https://templefranklin22.gumroad.com/l/vakzb",
  trueNorth: "https://templefranklin22.gumroad.com/l/xxykvq",
  bodyKnows: "https://templefranklin22.gumroad.com/l/hxhgh",
  kindClear: "https://templefranklin22.gumroad.com/l/gvkbv",
  fullMethod: "https://templefranklin22.gumroad.com/l/bxczrd",
};

const DECKS = [
  {
    id: "i-get-to",
    name: "I GET TO",
    cards: 55,
    framework: "TODAY, I GET TO\u2026",
    covers: "Daily Practice",
    image: "/i-get-to-deck/cover-card.png",
    page: "/i-get-to-deck",
    buyUrl: GUMROAD_URLS.iGetTo,
    accent: BRAND.gold,
  },
  {
    id: "rewire",
    name: "REWIRE",
    cards: "30+",
    framework: "OLD GROOVE \u2192 NEW PATH \u2192 REMEMBER",
    covers: "MIND",
    image: "/rewire-deck/cover-card.png",
    page: "/rewire-deck",
    buyUrl: GUMROAD_URLS.rewire,
    accent: BRAND.gold,
  },
  {
    id: "true-north",
    name: "TRUE NORTH",
    cards: 44,
    framework: "SAY IT \u2192 THE CALL \u2192 FEEL IT/ASK WHY",
    covers: "SPIRIT",
    image: "/true-north/cover-card.png",
    page: "/true-north",
    buyUrl: GUMROAD_URLS.trueNorth,
    accent: BRAND.gold,
  },
  {
    id: "body-knows",
    name: "THE BODY KNOWS",
    cards: 35,
    framework: "NOTICE \u2192 ASK \u2192 SHIFT",
    covers: "BODY",
    image: "/body-knows/cover-card.png",
    page: "/the-body-knows",
    buyUrl: GUMROAD_URLS.bodyKnows,
    accent: BRAND.gold,
  },
  {
    id: "kind-clear",
    name: "KIND & CLEAR",
    cards: 44,
    framework: "THE MOMENT \u2192 SAY THIS \u2192 WHY IT\u2019S KIND",
    covers: "HYGIENE",
    image: "/kind-clear-deck/cover-card.png",
    page: "/kind-clear-deck",
    buyUrl: GUMROAD_URLS.kindClear,
    accent: BRAND.gold,
  },
];

export default function TheMethodPage() {
  return (
    <div
      data-testid="the-method-page"
      style={{ backgroundColor: BRAND.cream, minHeight: "100vh", color: BRAND.tealInk }}
    >
      <Helmet>
        <meta
          name="keywords"
          content="mind body spirit hygiene, temple franklin, card decks, affirmation cards, somatic cards, boundary cards, neuroplasticity cards, i get to, rewire deck, true north deck, body knows deck, kind and clear deck, full method bundle, self regulation tools, printable cards pdf, women self help, messy middle"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "The Mind Body Spirit HYGIENE™ Method — All 5 Card Decks",
            description:
              "Five printable PDF card decks by Temple Franklin. Mind, Body, Spirit, Hygiene, and Daily Practice. $2.22 individual or $8.88 for the full bundle.",
            itemListElement: [
              {
                "@type": "Product",
                position: 1,
                name: "I GET TO — Daily Practice Card Deck",
                description:
                  "55 printable cards. Flips 'I have to' into 'I get to.' Daily ritual deck.",
                image:
                  "https://mindbodyspirithygienetools.com/i-get-to-deck/cover-card.png",
                brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
                offers: {
                  "@type": "Offer",
                  price: "2.22",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://templefranklin22.gumroad.com/l/wscmew",
                },
              },
              {
                "@type": "Product",
                position: 2,
                name: "REWIRE — Mind / Neuroplasticity Card Deck",
                description:
                  "30+ printable cards. OLD GROOVE → NEW PATH → REMEMBER framework for breaking mental loops.",
                image:
                  "https://mindbodyspirithygienetools.com/rewire-deck/cover-card.png",
                brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
                offers: {
                  "@type": "Offer",
                  price: "2.22",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://templefranklin22.gumroad.com/l/vakzb",
                },
              },
              {
                "@type": "Product",
                position: 3,
                name: "TRUE NORTH — Spirit / Inner Compass Card Deck",
                description:
                  "44 printable affirmation cards. SAY IT → THE CALL → FEEL IT/ASK WHY. WORTH, COURAGE, PEACE, TRUST.",
                image:
                  "https://mindbodyspirithygienetools.com/true-north/cover-card.png",
                brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
                offers: {
                  "@type": "Offer",
                  price: "2.22",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://templefranklin22.gumroad.com/l/xxykvq",
                },
              },
              {
                "@type": "Product",
                position: 4,
                name: "THE BODY KNOWS — Somatic Card Deck",
                description:
                  "35 printable somatic cards. NOTICE → ASK → SHIFT. GUT, HEART, TENSION, ENERGY.",
                image:
                  "https://mindbodyspirithygienetools.com/body-knows/cover-card.png",
                brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
                offers: {
                  "@type": "Offer",
                  price: "2.22",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://templefranklin22.gumroad.com/l/hxhgh",
                },
              },
              {
                "@type": "Product",
                position: 5,
                name: "KIND & CLEAR — Boundary Card Deck",
                description:
                  "44 printable boundary scripts. THE MOMENT → SAY THIS → WHY IT'S KIND.",
                image:
                  "https://mindbodyspirithygienetools.com/kind-clear-deck/cover-card.png",
                brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
                offers: {
                  "@type": "Offer",
                  price: "2.22",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://templefranklin22.gumroad.com/l/gvkbv",
                },
              },
              {
                "@type": "Product",
                position: 6,
                name: "The Full Method Bundle — All 5 Card Decks",
                description:
                  "The complete Mind Body Spirit HYGIENE™ Method. All 5 decks: I GET TO, REWIRE, TRUE NORTH, THE BODY KNOWS, KIND & CLEAR. Saves $2.22 vs buying separately.",
                image:
                  "https://mindbodyspirithygienetools.com/i-get-to-deck/cover-card.png",
                brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
                offers: {
                  "@type": "Offer",
                  price: "8.88",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://templefranklin22.gumroad.com/l/bxczrd",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      {/* ── HERO — all 5 decks fanned ─────────────────────── */}
      <section className="px-4 sm:px-6 pt-12 pb-6 md:pt-16 md:pb-10 max-w-7xl mx-auto text-center">
        <div
          className="inline-block uppercase text-xs md:text-sm px-5 py-2 rounded-full mb-5"
          style={{
            backgroundColor: BRAND.teal,
            color: BRAND.cream,
            letterSpacing: "0.3em",
          }}
        >
          ✦ The Method Is Here ✦
        </div>

        <h1
          className="text-4xl sm:text-5xl lg:text-6xl mb-3"
          style={{ fontFamily: "Georgia, serif", color: BRAND.tealDeep, lineHeight: 1.05 }}
        >
          The Mind Body Spirit HYGIENE&trade; Method
        </h1>

        <p
          className="text-lg md:text-xl italic mb-2"
          style={{ color: BRAND.tealDeep, opacity: 0.88, fontFamily: "Georgia, serif" }}
        >
          Five frameworks. One Method. All yours.
        </p>

        <p
          className="uppercase tracking-[0.3em] text-xs md:text-sm mt-3 mb-1"
          style={{ color: BRAND.gold, fontWeight: 600 }}
          data-testid="instant-pdf-subtitle"
        >
          ✦ Instant PDF Download ✦
        </p>

        <p className="text-base md:text-lg max-w-3xl mx-auto mt-5 mb-10 leading-relaxed opacity-85">
          The complete library of self-regulation tools by Temple Franklin. One deck
          for your <strong>mind</strong>, one for your <strong>body</strong>, one
          for your <strong>spirit</strong>, one for your <strong>relationships</strong>,
          and one for the <strong>daily practice</strong> that ties it all together.
        </p>

        {/* ── 5-DECK VISUAL LINEUP ──────────────────────────── */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-5 mb-10"
          data-testid="deck-lineup"
        >
          {DECKS.map((d, i) => (
            <Link
              key={d.id}
              to={d.page}
              data-testid={`lineup-card-${d.id}`}
              className="group block rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{
                border: `1px solid ${BRAND.gold}40`,
                transform: `rotate(${[-2, -1, 0, 1, 2][i]}deg)`,
              }}
            >
              <img
                src={d.image}
                alt={`${d.name} deck cover`}
                className="w-full h-auto block"
                loading="lazy"
              />
            </Link>
          ))}
        </div>

        {/* ── BUNDLE HERO CTA ──────────────────────────────── */}
        <div
          className="max-w-2xl mx-auto p-6 md:p-10 rounded-lg"
          style={{
            backgroundColor: BRAND.tealDeep,
            color: BRAND.cream,
            boxShadow: "0 25px 60px rgba(4,47,46,0.18)",
          }}
        >
          <p
            className="uppercase tracking-[0.3em] text-xs mb-2"
            style={{ color: BRAND.gold }}
          >
            ✦ The Full Method Bundle ✦
          </p>
          <h2
            className="text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            All 5 decks. One price.
          </h2>

          <div className="flex items-baseline justify-center gap-3 mb-2">
            <p
              className="text-5xl md:text-6xl"
              style={{ fontFamily: "Georgia, serif", fontWeight: 700 }}
            >
              $8.88
            </p>
            <p className="text-lg opacity-70 line-through">$11.10</p>
          </div>
          <p className="text-sm italic opacity-85 mb-7">
            Save $2.22 vs buying separately &middot; Instant PDF download
          </p>

          <a
            href={GUMROAD_URLS.fullMethod}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bundle-buy-btn"
            className="inline-block w-full sm:w-auto px-10 py-4 rounded-md uppercase tracking-[0.2em] text-sm md:text-base transition-opacity hover:opacity-90"
            style={{
              backgroundColor: BRAND.gold,
              color: BRAND.tealInk,
              fontWeight: 700,
            }}
          >
            Get all 5 decks &rarr;
          </a>

          <p className="text-xs italic mt-5 opacity-70">
            Or buy any deck individually below for $2.22 each.
          </p>
        </div>
      </section>

      {/* ── 5 INDIVIDUAL DECK CARDS ──────────────────────── */}
      <section className="px-4 sm:px-6 py-10 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-2"
            style={{ color: BRAND.gold }}
          >
            ✦ Or pick the deck you need today ✦
          </p>
          <h2
            className="text-2xl md:text-3xl"
            style={{ fontFamily: "Georgia, serif", color: BRAND.tealDeep }}
          >
            $2.22 per deck. Instant download.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {DECKS.map((d) => (
            <div
              key={d.id}
              data-testid={`deck-${d.id}`}
              className="rounded-lg overflow-hidden flex flex-col transition-transform hover:scale-[1.02]"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${BRAND.gold}40`,
                boxShadow: "0 8px 24px rgba(4,47,46,0.08)",
              }}
            >
              <img src={d.image} alt={d.name} className="w-full h-auto block" loading="lazy" />
              <div className="p-4 flex-1 flex flex-col">
                <p
                  className="uppercase tracking-[0.2em] text-xs mb-1"
                  style={{ color: BRAND.gold }}
                >
                  {d.covers}
                </p>
                <p
                  className="text-xl mb-1"
                  style={{ fontFamily: "Georgia, serif", color: BRAND.tealDeep, fontWeight: 700 }}
                >
                  {d.name}
                </p>
                <p className="text-xs opacity-70 mb-2">
                  {d.cards} cards &middot; {d.framework}
                </p>
                <p
                  className="text-[10px] uppercase tracking-[0.18em] mb-2"
                  style={{ color: BRAND.gold, fontWeight: 600 }}
                >
                  Instant PDF Download
                </p>
                <div className="mt-auto flex flex-col gap-2 pt-3">
                  <a
                    href={d.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`buy-${d.id}`}
                    className="block text-center px-3 py-2.5 rounded-md uppercase tracking-[0.15em] text-xs transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: d.buyUrl === "#" ? "#cbd5e1" : BRAND.tealDeep,
                      color: BRAND.cream,
                      fontWeight: 600,
                      pointerEvents: d.buyUrl === "#" ? "none" : "auto",
                    }}
                  >
                    {d.buyUrl === "#" ? "Coming soon" : "Buy · $2.22"}
                  </a>
                  <Link
                    to={d.page}
                    data-testid={`preview-${d.id}`}
                    className="block text-center text-xs underline"
                    style={{ color: BRAND.teal }}
                  >
                    See sneak peek &rarr;
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY THE METHOD ───────────────────────────────── */}
      <section
        className="px-6 py-14 md:py-16 mt-6"
        style={{ backgroundColor: BRAND.tealDeep, color: BRAND.cream }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-3"
            style={{ color: BRAND.gold }}
          >
            ✦ Why The Method ✦
          </p>
          <h2
            className="text-3xl md:text-4xl mb-5"
            style={{ fontFamily: "Georgia, serif" }}
          >
            You do not outsource brushing your teeth.
          </h2>
          <p className="text-base md:text-lg leading-relaxed opacity-95">
            You do not outsource your spiritual, mental, or emotional hygiene
            either. The work is yours &mdash; daily, non-negotiable, yours to do,
            by yourself, for yourself. The Mind Body Spirit HYGIENE&trade; Method
            gives you the framework and the tools &mdash; <em>you</em> do the
            work. <strong>That is the liberation forty years of seminars never
            delivered.</strong>
          </p>
          <p className="text-sm italic mt-7 opacity-80">
            &mdash; Temple Franklin
            <br />
            Founder, Mind Body Spirit HYGIENE&trade;
          </p>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <section className="px-6 py-8 text-center text-sm" style={{ opacity: 0.75 }}>
        <p>
          <Link
            to="/"
            data-testid="link-shop"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            Browse everything in the shop
          </Link>{" "}
          &middot;{" "}
          <Link
            to="/book"
            data-testid="link-book"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            The book
          </Link>{" "}
          &middot;{" "}
          <Link
            to="/draw"
            data-testid="link-draw"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            Pull a free card
          </Link>
        </p>
        <p className="mt-3 italic" style={{ opacity: 0.7 }}>
          Mind Body Spirit HYGIENE&trade; &middot; mindbodyspirithygiene.com
        </p>
      </section>
    </div>
  );
}
